// src/components/Layout/DrawerLayout.js
import React, { useState } from 'react';
import { 
  Drawer, 
  AppBar, 
  Toolbar, 
  IconButton, 
  Box, 
  CssBaseline, 
  Menu, 
  MenuItem,
  Typography,
  Avatar,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  ExitToApp as LogoutIcon,
  Person as ProfileIcon,
} from '@mui/icons-material';
import Sidebar from '../Sidebar';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import FeedbackModal from '../FeedbackModal';

const DrawerLayout = ({ children, onLogout }) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    onLogout();
    navigate('/login');
    handleClose();
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar 
        position="fixed" 
        elevation={0}
        sx={{ 
          zIndex: theme => theme.zIndex.drawer + 1,
          background: 'white',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        }}
      >
        <Toolbar 
          sx={{ 
            justifyContent: 'space-between',
            paddingBottom: 2,
            minHeight: '80px',
          }}
        >
          <Box display="flex" alignItems="center">
            <IconButton
              color="primary"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{
                mr: 2,
                display: { sm: 'none' },
                '&:hover': {
                  background: 'rgba(33, 150, 243, 0.08)',
                },
              }}
            >
              <MenuIcon />
            </IconButton>

            <Box 
              display="flex" 
              alignItems="center"
            >
              <img
                src="/autoblogpilotlogo.png"
                alt="AutoBlogPilot"
                style={{
                  height: 35,
                  width: 'auto',
                  marginRight: 8,
                  objectFit: 'contain',
                }}
              />
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: 'primary.main',
                }}
              >
                AutoBlogPilot
              </Typography>
            </Box>
          </Box>

          <Box>
            <Tooltip title="Account">
              <IconButton
                onClick={handleMenu}
                sx={{
                  p: 0.5,
                  border: '2px solid',
                  borderColor: 'primary.main',
                  '&:hover': {
                    background: 'rgba(33, 150, 243, 0.08)',
                  },
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: 'primary.main',
                    width: 32,
                    height: 32,
                  }}
                >
                  <AccountCircle />
                </Avatar>
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              PaperProps={{
                elevation: 0,
                sx: {
                  mt: 1.5,
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
                  '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                  },
                },
              }}
            >
              <MenuItem 
                onClick={() => {
                  handleClose();
                  navigate('/profile');
                }}
                sx={{
                  gap: 1.5,
                  minWidth: 160,
                }}
              >
                <ProfileIcon fontSize="small" />
                <Typography>Profile</Typography>
              </MenuItem>
              <MenuItem 
                onClick={handleLogout}
                sx={{
                  gap: 1.5,
                  color: 'error.main',
                }}
              >
                <LogoutIcon fontSize="small" />
                <Typography>Logout</Typography>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ x: -240 }}
            animate={{ x: 0 }}
            exit={{ x: -240 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            style={{
              position: 'fixed',
              zIndex: theme.zIndex.drawer,
              height: '100%',
            }}
          >
            <Drawer
              variant={isMobile ? 'temporary' : 'permanent'}
              open={open}
              onClose={handleDrawerToggle}
              ModalProps={{
                keepMounted: true,
              }}
              sx={{
                display: { xs: 'block', sm: 'none' },
                '& .MuiDrawer-paper': {
                  width: 240,
                  boxSizing: 'border-box',
                  border: 'none',
                },
              }}
            >
              <Sidebar />
            </Drawer>
          </motion.div>
        )}
      </AnimatePresence>

      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            width: 240,
            boxSizing: 'border-box',
            border: 'none',
          },
        }}
        open
      >
        <Sidebar />
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - 240px)` },
          ml: { sm: '240px' },
          mt: '80px',
        }}
      >
        {children}
      </Box>
      <FeedbackModal />
    </Box>
  );
};

export default DrawerLayout;
